import { ParamMap, Params } from '@angular/router';
import { MaintenanceTimerFilterDto } from '@artemis-software/wr-api';
import { parseBooleanParam } from '@/utils/url-utils';

export interface MaintenanceTimerDashboardFilterParams {
  tenantId?: string;
  buildingId?: string;
  from?: string;
  to?: string;
  expired: boolean;
  notExpired: boolean;
  maxDueDays?: number;
  timerTenantId?: string;
  equipmentId?: string;
  myTeamOnly: boolean;
}

export const DEFAULT_FILTER_PARAMS: MaintenanceTimerDashboardFilterParams = {
  expired: true,
  notExpired: true,
  myTeamOnly: true,
};

const PARAM_KEYS = [
  'tenantId',
  'buildingId',
  'from',
  'to',
  'expired',
  'notExpired',
  'maxDueDays',
  'timerTenantId',
  'equipmentId',
  'myTeamOnly',
] as const satisfies readonly (keyof MaintenanceTimerDashboardFilterParams)[];

export function parseFilterParams(params: ParamMap): MaintenanceTimerDashboardFilterParams | undefined {
  if (!PARAM_KEYS.some(key => params.has(key))) {
    return undefined;
  }
  const maxDueDays = params.get('maxDueDays');
  const from = params.get('from') ?? undefined;
  return {
    tenantId: params.get('tenantId') ?? undefined,
    buildingId: params.get('buildingId') ?? undefined,
    from,
    to: from ? (params.get('to') ?? undefined) : undefined,
    expired: parseBoolean(params.get('expired'), DEFAULT_FILTER_PARAMS.expired),
    notExpired: parseBoolean(params.get('notExpired'), DEFAULT_FILTER_PARAMS.notExpired),
    maxDueDays: maxDueDays && Number.isFinite(Number(maxDueDays)) ? Number(maxDueDays) : undefined,
    timerTenantId: params.get('timerTenantId') ?? undefined,
    equipmentId: params.get('equipmentId') ?? undefined,
    myTeamOnly: parseBoolean(params.get('myTeamOnly'), DEFAULT_FILTER_PARAMS.myTeamOnly),
  };
}

function parseBoolean(value: string | null, fallback: boolean): boolean {
  return parseBooleanParam(value) ?? fallback;
}

export function toQueryParams(params: MaintenanceTimerDashboardFilterParams): Params {
  const query: Params = {};
  for (const key of PARAM_KEYS) {
    const value = params[key];
    if (value !== undefined && value !== null && value !== '') {
      query[key] = String(value);
    }
  }
  return query;
}

export function sameQueryParams(a: Params, b: Params): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    if (String(a[key] ?? '') !== String(b[key] ?? '')) {
      return false;
    }
  }
  return true;
}

export function toFilterDto(
  params: MaintenanceTimerDashboardFilterParams,
  isExternalTenant: boolean,
): MaintenanceTimerFilterDto {
  let expired: boolean | undefined;
  if (params.expired && !params.notExpired) {
    expired = true;
  } else if (!params.expired && params.notExpired) {
    expired = false;
  }
  return {
    ownerTenantId: params.tenantId,
    buildingId: params.buildingId,
    equipmentId: params.equipmentId,
    timerTenantId: params.timerTenantId,
    fromDate: params.from,
    toDate: params.to ?? params.from,
    maxDueDays: params.maxDueDays,
    expired,
    myTeamOnly: isExternalTenant ? params.myTeamOnly : undefined,
  };
}
